import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Segmented } from "@/components/layout/segmented";
import type { PerfilComprador } from "@shared/schema";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { useCreateComprador, useUpdateComprador } from "@/hooks/use-vendas";
import { CHAVE_PRODUTOR } from "@/hooks/use-mercado";

/**
 * Cadastro do corretor = cadastro de `compradores` do Cacho. MESMA tabela, MESMA
 * API (`/api/compradores`), MESMO schema — não é um segundo cadastro. O que muda
 * é só de onde se abre: aqui, do card "+ novo comprador" e do "Editar" do detalhe.
 */
export function DialogComprador({
  comprador,
  focarTelefone,
  onFechar,
  onSalvo,
}: {
  /** Sem `comprador` = novo. */
  comprador?: { id: string; nome: string; telefone: string | null; cidade: string | null; perfil?: string };
  /**
   * Abre com o cursor no TELEFONE, e não no nome.
   *
   * É o caso de quem chegou por "Adicionar telefone": o nome já está certo, e
   * o que falta é o número. Focar o nome faria o produtor tabular por um campo
   * preenchido antes de chegar no que ele veio fazer.
   */
  focarTelefone?: boolean;
  onFechar: () => void;
  onSalvo?: (id: string) => void;
}) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const criar = useCreateComprador();
  const atualizar = useUpdateComprador();
  const [nome, setNome] = useState(comprador?.nome ?? "");
  const [telefone, setTelefone] = useState(comprador?.telefone ?? "");
  const [cidade, setCidade] = useState(comprador?.cidade ?? "");
  // Nasce CORRETOR: é o que a esmagadora maioria do cadastro é hoje, e o claim
  // corrige sozinho quando o próprio comprador se declara distribuidor.
  const [perfil, setPerfil] = useState<PerfilComprador>((comprador?.perfil as PerfilComprador) ?? "CORRETOR");
  const salvando = criar.isPending || atualizar.isPending;

  async function handleSalvar() {
    if (nome.trim().length < 2) {
      toast({ title: "Informe o nome do comprador", variant: "destructive" });
      return;
    }
    try {
      let id = comprador?.id;
      if (comprador) {
        await atualizar.mutateAsync({
          id: comprador.id,
          data: { nome: nome.trim(), telefone: telefone.trim() || null, cidade: cidade.trim() || null, perfil } as any,
        });
      } else {
        const novo = await criar.mutateAsync({
          nome: nome.trim(),
          telefone: telefone.trim() || undefined,
          cidade: cidade.trim() || undefined,
          perfil,
        });
        id = novo.id;
      }
      qc.invalidateQueries({ queryKey: [CHAVE_PRODUTOR] });
      toast({ title: comprador ? "Comprador atualizado!" : "Comprador cadastrado!" });
      if (id) onSalvo?.(id);
      onFechar();
    } catch (e: any) {
      toast({ title: "Não deu para salvar", description: e.message, variant: "destructive" });
    }
  }

  return (
    <Dialog open onOpenChange={aberto => { if (!aberto) onFechar(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{comprador ? "Editar comprador" : "Novo comprador"}</DialogTitle>
          <DialogDescription>
            É o mesmo cadastro de compradores do Cacho — aparece nas vendas também.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="comprador-nome">Nome</Label>
            <Input id="comprador-nome" value={nome} onChange={e => setNome(e.target.value)} autoFocus={!focarTelefone} data-testid="input-comprador-nome" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="comprador-telefone">Telefone</Label>
            <Input id="comprador-telefone" inputMode="tel" placeholder="(74) 99999-8888" value={telefone} onChange={e => setTelefone(e.target.value)} autoFocus={focarTelefone} data-testid="input-comprador-telefone" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="comprador-cidade">Cidade</Label>
            <Input id="comprador-cidade" value={cidade} onChange={e => setCidade(e.target.value)} data-testid="input-comprador-cidade" />
          </div>

          <div className="space-y-1.5">
            <Label>Perfil</Label>
            <Segmented<PerfilComprador>
              valor={perfil}
              onChange={setPerfil}
              prefixoTestId="seg-perfil"
              className="w-full [&>button]:flex-1"
              opcoes={[
                { id: "CORRETOR", label: "Corretor" },
                { id: "DISTRIBUIDOR", label: "Distribuidor" },
              ]}
            />
            <p className="text-[11px] text-muted-foreground">
              Corretor compra e revende; distribuidor compra para o próprio negócio.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onFechar}>Cancelar</Button>
          <Button onClick={handleSalvar} disabled={salvando} data-testid="button-comprador-salvar">
            {salvando ? "Salvando…" : "Salvar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
